import React from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import copilotStore from "Stores/copilotStore";
import userDetail from "Stores/userDetail";
import importFromContactsListPopupState from "Components/common/importFromContactsListPopupState";
import { makeApi, URLS } from "Utils/apiURL";
import { LOCAL_ACTIONS, openSupportChat } from "../../agent/agentContract";

const CtaChipsBlock = observer(({ block }) => {
  const history = useHistory();

  const handleClick = chip => {
    if (chip.actionId === LOCAL_ACTIONS.NAVIGATE && chip.path) {
      history.push(chip.path);
    }
    if (chip.actionId === LOCAL_ACTIONS.CONTACT_SUPPORT) {
      openSupportChat();
    }
    if (chip.actionId === LOCAL_ACTIONS.ADD_CONTACTS && chip.sequenceId) {
      // Same picker the sequence page uses, rather than listing every saved
      // contact in the thread: pick a list, import the whole list.
      const {
        planName = "",
        inFreePlan = false,
        noOfContactsEligible = 0,
        noOfContactsUsed = 0
      } = userDetail?.userFeatureFlag || {};
      // The guards inspect the sequence itself (it must have steps, it must not
      // be over its contact allowance), so the popup needs the real record --
      // an id-only stub trips "add steps first" on a sequence that has them.
      let loadedSequence = null;
      importFromContactsListPopupState.open({
        sequenceId: chip.sequenceId,
        sequence: { id: chip.sequenceId },
        title: "Add contacts",
        // The plan and credit limits are enforced inside the popup's guards --
        // without this context they cannot fire and an import could run past
        // the account's contact allowance.
        guardContext: {
          routerProps: { history },
          planName,
          inFreePlan,
          noOfContactsEligible,
          remainingContactCount: () => noOfContactsEligible - noOfContactsUsed
        },
        // Called with no arguments, so there is no count to quote here -- the
        // popup's own toast reports the numbers. Note only that it landed.
        afterGuards: async () => {
          try {
            const res = await makeApi({
              url: URLS.getSelectedSeqData.replace("<<seqID>>", chip.sequenceId),
              method: "GET"
            });
            loadedSequence = res?.data || null;
          } catch (e) {
            loadedSequence = null;
          }
        },
        refreshSequence: () => loadedSequence,
        onImportSuccess: () => {
          copilotStore.addLocalNote(
            "Contacts from that list have been added to the sequence."
          );
        }
      });
      return;
    }
    copilotStore.clickCta(block.id, chip);
  };

  return (
    <div className="copilotCtaChips">
      {(block.chips || []).map(chip => {
        const consumed =
          block.clickedActionId && block.clickedActionId === chip.actionId;
        const loading =
          chip.actionId === LOCAL_ACTIONS.ADD_CONTACTS &&
          Boolean(chip.sequenceId) &&
          copilotStore.addContactsPending === chip.sequenceId;
        // Local chips (view table, navigate) stay usable after an agent
        // action from the same row has been consumed.
        const isLocal =
          chip.actionId === LOCAL_ACTIONS.VIEW_TABLE ||
          chip.actionId === LOCAL_ACTIONS.NAVIGATE ||
          chip.actionId === LOCAL_ACTIONS.CONTACT_SUPPORT ||
          chip.actionId === LOCAL_ACTIONS.ADD_CONTACTS;
        return (
          <button
            type="button"
            key={chip.actionId + (chip.label || "")}
            className={`copilotCtaChips__chip ${
              chip.style === "primary" ? "primary" : ""
            } ${consumed ? "consumed" : ""}`}
            disabled={
              loading ||
              copilotStore.streaming ||
              (Boolean(block.clickedActionId) && !isLocal)
            }
            onClick={() => handleClick(chip)}
          >
            {chip.icon && !consumed && !loading && (
              <i className="material-icons-outlined">{chip.icon}</i>
            )}
            {loading && <span className="copilotCtaChips__spin" />}
            <span>{loading ? "Loading contacts…" : chip.label}</span>
            {consumed && <i className="material-icons-outlined">check</i>}
          </button>
        );
      })}
    </div>
  );
});

export default CtaChipsBlock;
